import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Phone, Mail, CheckCircle2 } from "lucide-react";

const VerifyCard = ({ channel, icon: Icon, label, target, verified, onVerified }) => {
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const send = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/verify/send", { channel });
      setSent(true);
      if (data.dev_code) {
        toast.info(`Demo mode: your ${label} code is ${data.dev_code}`, { duration: 8000 });
        setCode(data.dev_code);
      } else {
        toast.success(`Code sent to your ${label}.`);
      }
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const check = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/verify/check", { channel, code });
      onVerified(data);
      toast.success(`${label} verified!`);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-3xl border border-linen-3 p-6" data-testid={`verify-${channel}-card`}>
      <div className="flex items-center gap-3 mb-1">
        <span className="w-10 h-10 rounded-2xl bg-calm flex items-center justify-center">
          {verified ? <CheckCircle2 className="w-5 h-5 text-brand" /> : <Icon className="w-5 h-5 text-brand" />}
        </span>
        <div>
          <p className="font-medium text-ink">{label}</p>
          <p className="text-xs text-ink-mute">{target}</p>
        </div>
      </div>
      {verified ? (
        <p className="text-sm text-brand mt-3 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Verified</p>
      ) : !sent ? (
        <Button onClick={send} disabled={loading} className="mt-3 w-full rounded-full bg-brand hover:bg-brand-hover text-linen" data-testid={`verify-${channel}-send`}>
          {loading ? "Sending…" : `Send code`}
        </Button>
      ) : (
        <div className="mt-3 flex gap-2">
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code" className="rounded-2xl bg-linen border-linen-3" data-testid={`verify-${channel}-input`} />
          <Button onClick={check} disabled={loading} className="rounded-full bg-accent hover:bg-accent-hover text-linen px-5" data-testid={`verify-${channel}-check`}>Verify</Button>
        </div>
      )}
    </div>
  );
};

const Verify = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const bothDone = user?.phone_verified && user?.email_verified;

  return (
    <div className="min-h-screen bg-linen flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <h1 className="font-serif text-4xl text-brand text-center mb-2">Verify it's you</h1>
        <p className="text-center text-ink-mute mb-8">A quick step to keep your safety net working.</p>
        <div className="space-y-4">
          <VerifyCard channel="phone" icon={Phone} label="Phone" target={user?.phone} verified={user?.phone_verified} onVerified={setUser} />
          <VerifyCard channel="email" icon={Mail} label="Email" target={user?.email} verified={user?.email_verified} onVerified={setUser} />
        </div>
        <Button onClick={() => navigate("/gateway")} className="mt-8 w-full rounded-full bg-brand hover:bg-brand-hover text-linen h-12" data-testid="verify-continue">
          {bothDone ? "Enter Aurea" : "Continue for now"}
        </Button>
        {!bothDone && <p className="text-center text-xs text-ink-mute mt-3">You can complete verification anytime in Settings.</p>}
      </div>
    </div>
  );
};

export default Verify;
